import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { useGetMaintenanceModeQuery } from 'store/services/admin/adminApi';
import { selectShouldCheckMaintenance, clearMaintenanceCheck, selectIsMaintenanceModeActive, setMaintenanceMode } from 'store/slices/maintenanceSlice';
import { useDispatch } from 'react-redux';
import MaintenancePage from 'screens/Maintenance/MaintenancePage';
import type { RootState } from 'store';

interface Props {
  children: React.ReactNode;
}

const MaintenanceGuard: React.FC<Props> = ({ children }) => {
  const dispatch = useDispatch();
  const location = useLocation(); // Force re-render on route changes
  const token = useSelector((state: RootState) => state.auth.token);
  const user = useSelector((state: RootState) => state.auth.user);
  const isAuthenticated = token || user;

  // Check admin status directly from Redux state (user data is populated on login)
  const userRoles = user?.roles || [];
  const isAdmin = userRoles.includes('admin');

  const shouldForceCheck = useSelector((state: RootState) => selectShouldCheckMaintenance(state));

  // Get maintenance status from Redux (set by 503 detection in baseQuery)
  const maintenanceModeFromRedux = useSelector(selectIsMaintenanceModeActive);

  // Query maintenance mode status with polling
  // Only query if user is admin (non-admins get 403 on this endpoint)
  const { data: maintenanceModeData, refetch } = useGetMaintenanceModeQuery(undefined, {
    pollingInterval: 60000, // Check every 60 seconds
    skip: !isAuthenticated || !isAdmin, // Only query if authenticated AND admin
  });

  // Update Redux when admin query succeeds
  useEffect(() => {
    if (maintenanceModeData && isAdmin) {
      dispatch(setMaintenanceMode(maintenanceModeData.maintenanceMode));
    }
  }, [maintenanceModeData, isAdmin, dispatch]);

  // Handle forced maintenance checks (triggered by 503 errors)
  useEffect(() => {
    if (shouldForceCheck && isAuthenticated && isAdmin) {
      // Force an immediate refetch when a 503 error is detected (only for admins)
      refetch();
      // Clear the flag after refetching
      dispatch(clearMaintenanceCheck());
    } else if (shouldForceCheck) {
      // For non-admins, just clear the flag (503 already set Redux state)
      dispatch(clearMaintenanceCheck());
    }
  }, [shouldForceCheck, isAuthenticated, isAdmin, refetch, dispatch]);

  // Combine both sources: Redux state (from 503) and query data (from admin endpoint)
  const isMaintenanceActive = maintenanceModeFromRedux || maintenanceModeData?.maintenanceMode;

  // Log every render to debug blocking
  console.log('MaintenanceGuard render:', {
    pathname: location.pathname,
    maintenanceModeFromRedux,
    maintenanceModeFromQuery: maintenanceModeData?.maintenanceMode,
    isMaintenanceActive,
    isAdmin,
    userRoles,
    isAuthenticated,
    shouldBlock: isMaintenanceActive && !isAdmin
  });

  // If maintenance mode is on and user is not admin, show maintenance page
  if (isMaintenanceActive && !isAdmin) {
    console.log('🚨 BLOCKING USER - Showing maintenance page');
    return <MaintenancePage />;
  }

  // Otherwise, render children (normal routes)
  return <>{children}</>;
};

export default MaintenanceGuard;
